import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Autocomplete,
  MenuItem,
  Select,
  Link,
  Tooltip,
  SelectChangeEvent,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  AddressDropdownItem,
  CheckoutResponse,
  OptionGroup,
  SavedDeliveryPickupPayload,
} from "@/app/models/checkout/types";
import InfoIcon from "@mui/icons-material/Info";
import {
  DatePicker,
  DesktopDatePicker,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  EagleViewAddressPrefill,
  STORAGE_KEYS,
} from "@/app/utils/localStorageHelper/checkoutKeys";
import {
  clearFromStorage,
  loadFromStorage,
  saveToStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import { RootState } from "@/app/store/store";
import { useSelector } from "react-redux";
import CommonHelper from "@/app/helpers/CommonHelper";
export interface PickupDeliveryProps {
  open: boolean;
  model: CheckoutResponse | null;
  onSave: (payload: SavedDeliveryPickupPayload) => void;
  isEagleViewLocked: boolean;
}
export const HOURS_LIMIT = 14;
export const TimeOptions = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "anytime", label: "Anytime" },
];

export function getNextBusinessDay(
  holidays: string[],
  hoursLimit: number = 14
): dayjs.Dayjs {
  const holidaySet = new Set(holidays);
  let d = dayjs();

  if (d.hour() >= hoursLimit) {
    d = d.add(1, "day");
  }

  while (
    d.day() === 0 ||
    d.day() === 6 ||
    holidaySet.has(d.format("YYYY-MM-DD"))
  ) {
    d = d.add(1, "day");
  }

  return dayjs(d.format("YYYY-MM-DD"));
}

export function validPickupTime(time: string, pickupDate: Dayjs) {
  if (!pickupDate) return false;

  const disabled = new Set<string>();
  const now = dayjs();
  const today = now.startOf("day");
  const tomorrow = today.add(1, "day");

  if (pickupDate.isAfter(tomorrow, "day")) {
  } else if (now.hour() >= HOURS_LIMIT) {
    disabled.add(TimeOptions[0].value);
    disabled.add(TimeOptions[2].value);
  } else if (pickupDate.isSame(today, "day")) {
    disabled.add(TimeOptions[0].value);
    disabled.add(TimeOptions[2].value);
  }
  return !disabled.has(time);
}

export const getRenderDayWithTooltip = (
  holidays: string[],
  mode: "pickup" | "delivery"
) => {
  const holidaySet = new Set(holidays);
  const today = dayjs().startOf("day");

  return (
    day: Dayjs,
    _selectedDays: Array<Dayjs | null>,
    pickersDayProps: PickersDayProps<Dayjs>
  ) => {
    const isPast = day.isBefore(today, "day");
    const isToday = day.isSame(today, "day");
    const isWeekend = day.day() === 0 || day.day() === 6;
    const isHoliday = holidaySet.has(day.format("YYYY-MM-DD"));
    const isAfterCutOff =
      dayjs().hour() >= HOURS_LIMIT && pickersDayProps.disabled;

    let tooltipText = "";

    if (isPast) {
      tooltipText = "You cannot select a day in the past.";
    } else if (isToday && !isHoliday && mode === "delivery") {
      tooltipText = "You cannot select the present day.";
    } else if (isWeekend) {
      tooltipText = "You cannot select a weekend day.";
    } else if (isHoliday) {
      tooltipText = "Delivery/Pickup unavailable on holidays.";
    } else if (isAfterCutOff && mode === "delivery") {
      tooltipText = "Deadline for next day delivery has passed.";
    } else if (isAfterCutOff && mode === "pickup") {
      tooltipText = "Deadline for today pickup has passed.";
    }

    const combinedDisabled = pickersDayProps.disabled || !!tooltipText;
    const dayComponent = (
      <PickersDay
        {...pickersDayProps}
        disabled={combinedDisabled}
        sx={{ pointerEvents: "auto" }}
      />
    );

    return tooltipText ? (
      <Tooltip
        title={
          <span
            style={{
              display: "inline-block",
              inlineSize: "clamp(62px, 18ch, 100px)",
              textAlign: "center",
              whiteSpace: "normal",
              wordBreak: "normal",
              overflowWrap: "normal",
              hyphens: "manual",
              lineHeight: 1.25,
            }}
          >
            {tooltipText}
          </span>
        }
        componentsProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -10],
                },
              },
            ],
          },
          tooltip: {
            sx: {
              maxWidth: "max-content",
              bgcolor: "#fff",
              color: "red",
              border: "1px solid red",
              fontSize: 13,
              fontWeight: 500,
              padding: "6px 8px",
              textAlign: "center",
            },
          },
        }}
      >
        <span>{dayComponent}</span>
      </Tooltip>
    ) : (
      dayComponent
    );
  };
};

export const PickupDelivery: React.FC<PickupDeliveryProps> = ({
  open,
  model,
  onSave,
  isEagleViewLocked,
}) => {
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";
  var defaultTimeOption = TimeOptions[0].value;
  const cached = loadFromStorage<SavedDeliveryPickupPayload>(
    userId,
    STORAGE_KEYS.PICKUP_DELIVERY
  );
  if (cached) {
    defaultTimeOption =
      TimeOptions.find((opt) => opt.value === cached.selectedTime)?.value ??
      TimeOptions[0].value;
  }
  clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
  const eagleViewPrefill = loadFromStorage<EagleViewAddressPrefill>(
    userId,
    STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL
  );
  const NEW_ADDRESS: AddressDropdownItem = {
    id: "0",
    name: "New Address - Fill in Address Below",
  };
  const [deliverySavedAddresses, setDeliverySavedAddresses] = useState<
    OptionGroup[]
  >([{ label: "", options: [NEW_ADDRESS] }]);
  const [selectedValue, setSelectedValue] = useState<string>("0");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState<string | null>(
    model?.shippingAddress.shippingNewAddress.availableStates[0].value ?? ""
  );
  const [zip, setZip] = useState("");
  const [phone, _setPhone] = useState(
    CommonHelper.formatPhone334(
      model?.shippingAddress.shippingNewAddress?.phoneNumber ?? ""
    )
  );

  const setPhone = (value: string) => {
    _setPhone(CommonHelper.formatPhone334(value));
  };

  const [notes, setNotes] = useState(cached?.note ?? "");
  const [showPickup, setShowPickup] = useState(isEagleViewLocked ? false : (cached?.isPickup ?? true));
  const holidays = model?.shippingAddress.publicHolidays ?? [];
  const holidaySet = useMemo(
    () =>
      new Set(
        (model?.shippingAddress.publicHolidays ?? [])
          .map((h) => dayjs(h, "YYYY-MM-DD", true))
          .filter((d) => d.isValid())
          .map((d) => d.format("YYYY-MM-DD"))
      ),
    [holidays]
  );
  dayjs.extend(utc);
  dayjs.extend(timezone);

  //pickup dateconst
  const minPickupDate = useMemo(() => getNextBusinessDay(holidays), []);
  const [pickupDate, setPickupDate] = useState<Dayjs>(
    dayjs(cached?.selectedDate) ?? minPickupDate
  );
  const [pickupDatePickerOpen, setPickupDatePickerOpen] = useState(false);
  const [pickupTime, setPickupTime] = useState<string>(defaultTimeOption);
  const [disabledPickupTimes, setDisabledPickupTimes] = useState<Set<string>>(
    new Set()
  );

  //delivery date
  const minDeliveryDate = useMemo(() => {
    const now = dayjs();
    return calculateMinDeliveryDate(now, HOURS_LIMIT, holidays);
  }, [holidays]);
  const [deliveryDate, setDeliveryDate] = useState<Dayjs>(
    dayjs(cached?.selectedDate) ?? minDeliveryDate
  );
  const [deliveryDatePickerOpen, setDeliveryDatePickerOpen] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState<string>(defaultTimeOption);

  const hasHours =
    model?.pickupPoint?.openingHours &&
    model?.pickupPoint?.openingHours.length > 0;
  const cityStateZip =
    (model?.pickupPoint.city || "") +
    (model?.pickupPoint.stateName ? ", " + model.pickupPoint.stateName : "") +
    (model?.pickupPoint.zipPostalCode
      ? ", " + model.pickupPoint.zipPostalCode
      : "");

  const { setNotification } = useGlobalNotification();

  useEffect(() => {
    const existing: AddressDropdownItem[] =
      model?.shippingAddress.existingAddresses?.map((addr: any) => {
        let addressLine = `${addr.firstName ?? ""} ${
          addr.lastName ?? ""
        }`.trim();
        if (addr.address1) addressLine += `, ${addr.address1}`;
        if (addr.city) addressLine += `, ${addr.city}`;
        if (addr.county) addressLine += `, ${addr.county}`;
        if (addr.stateProvinceName)
          addressLine += `, ${addr.stateProvinceName}`;
        if (addr.zipPostalCode) addressLine += ` ${addr.zipPostalCode}`;
        if (addr.countryName?.trim()) addressLine += `, ${addr.countryName}`;

        return { id: String(addr.id), name: addressLine };
      }) ?? [];
    setDeliverySavedAddresses([
      { label: "", options: [...existing, NEW_ADDRESS] },
    ]);
    setSelectedValue("0");
    if (cached?.deliveryAddress) {
      if (cached.deliveryAddress.shippingAddressId) {
        setSelectedValue(
          cached.deliveryAddress.shippingAddressId.toString() ?? "0"
        );
      } else {
        var newAddress = cached?.deliveryAddress;
        setFirstName(newAddress?.firstName ?? "");
        setLastName(newAddress?.lastName ?? "");
        setAddress1(newAddress?.address1 ?? "");
        setAddress2(newAddress?.address2 ?? "");
        setCompany(newAddress?.company ?? "");
        setCity(newAddress?.city ?? "");
        setZip(newAddress?.zip ?? "");
        setPhone(newAddress?.phone ?? "");
        setState(
          newAddress?.state != null
            ? String(newAddress.state)
            : model?.shippingAddress.shippingNewAddress.availableStates.find(
                (s) => s.text === newAddress?.stateName
              )?.value ?? null
        );
      }
    }

    if (eagleViewPrefill) {
      saveToStorage(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL, eagleViewPrefill);
    }

    if (eagleViewPrefill && !cached?.deliveryAddress) {
      setAddress1(eagleViewPrefill.address1);
      setCity(eagleViewPrefill.city);
      setZip(eagleViewPrefill.zip);
      setShowPickup(false);
      const resolvedState =
        model?.shippingAddress.shippingNewAddress.availableStates.find(
          (s) => s.text === eagleViewPrefill.stateName
        )?.value ?? null;
      setState(resolvedState);
    }

    if (!cached && !eagleViewPrefill && !isEagleViewLocked) {
      var isPickup =
        loadFromStorage<string>(userId, STORAGE_KEYS.CART_PICKUP_DELIVERY) ==
        "pickup";
      setShowPickup(isPickup);
    }

    if (deliveryDate < minDeliveryDate) {
      setDeliveryDate(minDeliveryDate);
    }

    if (pickupDate < minPickupDate) {
      setPickupDate(minPickupDate);
    }

    if (!validPickupTime(pickupTime, pickupDate)) {
      handlePickupTime(pickupDate);
    }
  }, []);

  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, "");
    const normalized =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

    const match = normalized.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const openLocationInMaps = () => {
    var searchAddress = model?.pickupPoint.address;
    var mapUrl =
      "https://www.google.com/maps/search/?api=1&query=" +
      searchAddress +
      " " +
      cityStateZip;
    const newWindow = window.open(mapUrl, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.focus();
  };

  const handlePickupDateChange = (newValue: Dayjs | null) => {
    if (!newValue) {
      setDisabledPickupTimes(new Set());
      setPickupTime(TimeOptions[0].value);
      return;
    }
    setPickupDate(newValue);
    handlePickupTime(newValue);
  };

  function handlePickupTime(newValue: Dayjs | null) {
    if (!newValue) {
      setPickupTime(TimeOptions[0].value);
      return;
    }
    const now = dayjs();
    const today = now.startOf("day");
    const tomorrow = today.add(1, "day");

    const disabled = new Set<string>();

    if (newValue.isAfter(tomorrow, "day")) {
    } else if (now.hour() >= HOURS_LIMIT) {
      disabled.add(TimeOptions[0].value);
      disabled.add(TimeOptions[2].value);
    } else if (newValue.isSame(today, "day")) {
      disabled.add(TimeOptions[0].value);
      disabled.add(TimeOptions[2].value);
    }

    setDisabledPickupTimes(disabled);

    const firstEnabled = TimeOptions.find((t) => !disabled.has(t.value));
    setPickupTime(firstEnabled?.value ?? TimeOptions[0].value);
  }

  const shouldDisablePickupDate = useCallback(
    (day: Dayjs) => {
      const isWeekend = day.day() === 0 || day.day() === 6;
      const isHoliday = holidaySet.has(day.format("YYYY-MM-DD"));
      const beforeMin = day.isBefore(minPickupDate, "day");
      return isWeekend || isHoliday || beforeMin;
    },
    [holidaySet, minPickupDate]
  );

  const handlePickupTimeChange = (e: SelectChangeEvent<string>) => {
    setPickupTime(e.target.value);
  };

  const handleDeliveryTimeChange = (e: SelectChangeEvent<string>) => {
    setDeliveryTime(e.target.value);
  };

  const handleDeliveryDateChange = (newValue: Dayjs | null) => {
    setDeliveryDate(newValue ?? minDeliveryDate);
  };

  function calculateMinDeliveryDate(
    now: dayjs.Dayjs,
    hoursLimit: number,
    holidays: string[]
  ): dayjs.Dayjs {
    const fmt = (d: dayjs.Dayjs) => d.format("YYYY-MM-DD");
    const isWeekend = (d: dayjs.Dayjs) => d.day() === 0 || d.day() === 6;
    const isHoliday = (d: dayjs.Dayjs) => holidays.includes(fmt(d));

    const isAfterCutoff = now.hour() >= hoursLimit;

    let d = now;
    let needsExtraIteration = false;

    if (isAfterCutoff || isWeekend(d) || isHoliday(d)) {
      d = d.add(1, "day");
      needsExtraIteration = true;
    } else {
      d = d.add(1, "day");
    }

    while (true) {
      const wknd = isWeekend(d);
      const hol = isHoliday(d);

      if (wknd || hol) {
        d = d.add(1, "day");
        continue;
      }

      if (needsExtraIteration) {
        d = d.add(1, "day");
        needsExtraIteration = false;
        continue;
      }
      break;
    }
    return d.startOf("day");
  }

  const shouldDisableDeliveryDate = useCallback(
    (day: Dayjs) => {
      const isWeekend = day.day() === 0 || day.day() === 6;
      const isHoliday = holidaySet.has(day.format("YYYY-MM-DD"));
      const beforeMin = day.isBefore(minDeliveryDate, "day");
      return isWeekend || isHoliday || beforeMin;
    },
    [holidaySet, minDeliveryDate]
  );

  const handleContinue = () => {
    var isCustomAddress = selectedValue == "0";
    if (!showPickup && isCustomAddress) {
      if (!address1 || !zip || !city || !state) {
        setNotification(
          "Please fill out all required fields in order to proceed with your order checkout.",
          "error"
        );
        return;
      }

      const isZipNumeric = /^\d+$/.test(zip);
      if (!isZipNumeric || zip.length !== 5) {
        setNotification(
          "Zip/Postal Code must be numerical and 5 digits.",
          "error"
        );
        return;
      }
    }

    var normalizedPhone = CommonHelper.normalizedPhone(phone);

    var existingAddress = isCustomAddress
      ? null
      : model?.shippingAddress.existingAddresses.find(
          (x) => x.id.toString() == selectedValue
        );
    onSave({
      isPickup: showPickup,
      note: notes,
      isEagleViewDelivery: isEagleViewLocked,
      selectedDate:
        (showPickup ? pickupDate : deliveryDate)?.format("MM/DD/YYYY") ?? "",
      selectedTime: showPickup ? pickupTime : deliveryTime,
      ...(showPickup && {
        pickupLocation: {
          name: model?.pickupPoint.name || "",
          address: model?.pickupPoint.address || "",
          cityStateZip: cityStateZip,
          phone: model?.pickupPoint.contactPhoneNumber || "",
          pickupFee: model?.pickupPoint.pickupFee || "",
          openingHours: model?.pickupPoint.openingHours,
        },
      }),
      ...(!showPickup && {
        deliveryAddress: {
          shippingAddressId: isCustomAddress
            ? null
            : existingAddress?.id ?? null,
          company: isCustomAddress ? company : existingAddress?.company ?? "",
          firstName: isCustomAddress
            ? firstName
            : existingAddress?.firstName ?? "",
          lastName: isCustomAddress
            ? lastName
            : existingAddress?.lastName ?? "",
          address1: isCustomAddress
            ? address1
            : existingAddress?.address1 ?? "",
          address2: isCustomAddress
            ? address2
            : existingAddress?.address2 ?? "",
          city: isCustomAddress ? city : existingAddress?.city ?? "",
          state: isCustomAddress
            ? state !== null && state !== undefined
              ? parseInt(state as string, 10)
              : null
            : existingAddress?.stateProvinceId ?? null,
          stateName: isCustomAddress
            ? state !== null && state !== undefined
              ? model?.shippingAddress.shippingNewAddress.availableStates.find(
                  (x) => x.value == state
                )?.text ?? ""
              : null
            : existingAddress?.stateProvinceName ?? null,
          zip: isCustomAddress ? zip : existingAddress?.zipPostalCode ?? "",
          phone: isCustomAddress
            ? normalizedPhone.length == 10
              ? normalizedPhone
              : ""
            : existingAddress?.phoneNumber ?? "",
        },
      }),
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box className="circleNumCheckout" sx={{ mr: 2 }}>
          <Typography className="circleNumCheckoutTypo">2</Typography>
        </Box>
        {!showPickup ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography className="checkout-container-title">
              Delivery
            </Typography>
            <Divider className="checkout-divider" />
            {open && !isEagleViewLocked && (
              <Button
                variant="text"
                color="primary"
                startIcon={<EditIcon />}
                sx={{ textTransform: "none" }}
                onClick={(e) => {
                  e.preventDefault();
                  const current = loadFromStorage<string>(
                    userId,
                    STORAGE_KEYS.CART_PICKUP_DELIVERY
                  );
                  if (current == null || current === "delivery") {
                    saveToStorage(
                      userId,
                      STORAGE_KEYS.CART_PICKUP_DELIVERY,
                      "pickup"
                    );
                  }
                  setShowPickup(true);
                }}
                component="a"
                href="#"
              >
                Switch to Pickup
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography className="checkout-container-title">
              Store Pickup
            </Typography>
            <Divider className="checkout-divider" />
            {open && (
              <Button
                variant="text"
                color="primary"
                startIcon={<EditIcon />}
                sx={{ textTransform: "none" }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowPickup(false);
                  const current = loadFromStorage<string>(
                    userId,
                    STORAGE_KEYS.CART_PICKUP_DELIVERY
                  );
                  if (current == null || current === "pickup") {
                    saveToStorage(
                      userId,
                      STORAGE_KEYS.CART_PICKUP_DELIVERY,
                      "delivery"
                    );
                  }
                }}
                component="a"
                href="#"
              >
                Switch to Delivery
              </Button>
            )}
          </Box>
        )}
      </Box>
      {open && (
        <Box
          sx={{
            border: "10px solid",
            borderColor: "grey.300",
            borderRadius: 2,
            p: 3,
          }}
        >
          {showPickup ? (
            <Box id="pickup-box">
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Pickup Location
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography fontWeight="bold" sx={{ mr: 1 }}>
                  {model?.pickupPoint.name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {hasHours && (
                    <Tooltip
                      title={
                        <Box display="flex" flexDirection="column">
                          {model.pickupPoint.openingHours?.map(
                            (hour, index) => (
                              <Typography
                                key={index}
                                variant="caption"
                                sx={{ fontSize: "10px", color: "white" }}
                              >
                                {hour}
                              </Typography>
                            )
                          )}
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <InfoIcon
                        sx={{
                          fontSize: 20,
                          color: "primary.main",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  )}
                  <Typography className="primary-branch">
                    Primary Branch
                  </Typography>
                </Box>
              </Box>
              <Typography>{model?.pickupPoint.address}</Typography>
              <Typography>{cityStateZip}</Typography>
              <Link
                href={`tel:${model?.pickupPoint.contactPhoneNumber}`}
                underline="hover"
              >
                {formatPhoneNumber(model?.pickupPoint.contactPhoneNumber ?? "")}
              </Link>
              {model?.pickupPoint.pickupFee != null && (
                <Typography sx={{ mb: 2 }}>
                  {model.pickupPoint.pickupFee}
                </Typography>
              )}
              <Box sx={{ height: 24 }} />
              <Button
                variant="outlined"
                color="primary"
                className="blueButtonOutline"
                onClick={openLocationInMaps}
              >
                GET DIRECTIONS
              </Button>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="medium" gutterBottom>
                      Date Needed
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        className="form-input"
                        value={pickupDate}
                        minDate={minPickupDate}
                        open={pickupDatePickerOpen}
                        shouldDisableDate={shouldDisablePickupDate}
                        onOpen={() => setPickupDatePickerOpen(true)}
                        onClose={() => setPickupDatePickerOpen(false)}
                        onChange={(newValue) =>
                          handlePickupDateChange(newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                            {...params}
                            size="small"
                            onClick={() => setPickupDatePickerOpen(true)}
                          />
                        )}
                        renderDay={getRenderDayWithTooltip(holidays, "pickup")}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="medium" gutterBottom>
                      Time Needed
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={pickupTime}
                        onChange={handlePickupTimeChange}
                      >
                        {TimeOptions.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            value={opt.value}
                            disabled={disabledPickupTimes.has(opt.value)}
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ height: 32 }} />
              <Box sx={{ height: 32 }} />
              <Typography fontWeight="600" gutterBottom fontSize={14}>
                Order Notes: **Please do not enter credit card number or other
                confidential information**
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                minRows={3}
                maxRows={5}
                inputProps={{ maxLength: 2000 }}
                value={notes}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => setNotes(e.target.value)}
              />
            </Box>
          ) : (
            <Box
              id="delivery-box"
              sx={isEagleViewLocked ? { pointerEvents: "none", opacity: 0.6 } : undefined}
            >
              <Box sx={{ mb: 3 }}>
                <Typography fontWeight="medium" gutterBottom>
                  Saved Addresses
                </Typography>
                <Autocomplete<AddressDropdownItem, false, true, false>
                  disableClearable
                  fullWidth
                  size="small"
                  getOptionKey={(option) => option.id}
                  options={deliverySavedAddresses.flatMap((g) => g.options)}
                  groupBy={(option) => {
                    const group = deliverySavedAddresses.find((g) =>
                      g.options.some((o) => o.id === option.id)
                    );
                    return group?.label ?? "";
                  }}
                  getOptionLabel={(option) => option.name}
                  value={deliverySavedAddresses
                    .flatMap((g) => g.options)
                    .find((opt) => opt.id == selectedValue)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  onChange={(_, newValue) => {
                    setSelectedValue(newValue?.id ?? "0");
                  }}
                  componentsProps={{
                    popper: {
                      sx: {
                        zIndex: 10000,
                      },
                    },
                  }}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <div style={{ fontWeight: "bold", padding: "0px 15px" }}>
                        {params.group}
                      </div>
                      <ul style={{ paddingLeft: 16 }}>{params.children}</ul>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      fullWidth
                      className="form-input"
                      size="small"
                    />
                  )}
                />
              </Box>
              {selectedValue === "0" && (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Delivery location
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={12}>
                      <Typography fontWeight="medium" gutterBottom>
                        Company
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography fontWeight="medium" gutterBottom>
                        First Name
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography fontWeight="medium" gutterBottom>
                        Last Name
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          fontWeight="medium"
                          gutterBottom
                          sx={{ mr: 0.5 }}
                        >
                          Address 1
                        </Typography>
                        <Typography sx={{ color: "error.main" }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={address1}
                        onChange={(e) => setAddress1(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography fontWeight="medium" gutterBottom>
                        Address 2
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          fontWeight="medium"
                          gutterBottom
                          sx={{ mr: 0.5 }}
                        >
                          City
                        </Typography>
                        <Typography sx={{ color: "error.main" }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          fontWeight="medium"
                          gutterBottom
                          sx={{ mr: 0.5 }}
                        >
                          State / Province
                        </Typography>
                        <Typography sx={{ color: "error.main" }}>*</Typography>
                      </Box>
                      <FormControl fullWidth size="small">
                        <Select
                          labelId="state-label"
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                        >
                          {model?.shippingAddress.shippingNewAddress.availableStates.map(
                            (option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          fontWeight="medium"
                          gutterBottom
                          sx={{ mr: 0.5 }}
                        >
                          Zip / Postal code
                        </Typography>
                        <Typography sx={{ color: "error.main" }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 5 }}
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        fontWeight="medium"
                        gutterBottom
                        sx={{ mr: 0.5 }}
                      >
                        Phone Number
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  fontWeight="bold"
                  gutterBottom
                >
                  Delivery Details
                </Typography>
                <Typography sx={{ color: "#B86125", fontSize: 16 }}>
                  Delivery will be confirmed following branch approval
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="medium" gutterBottom>
                    Date Needed
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      className="form-input"
                      value={deliveryDate}
                      minDate={minDeliveryDate}
                      open={deliveryDatePickerOpen}
                      shouldDisableDate={shouldDisableDeliveryDate}
                      onOpen={() => setDeliveryDatePickerOpen(true)}
                      onClose={() => setDeliveryDatePickerOpen(false)}
                      onChange={(newValue) =>
                        handleDeliveryDateChange(newValue)
                      }
                      PopperProps={{
                        placement: "bottom-start",
                      }}
                      renderInput={(params) => (
                        <TextField
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                          {...params}
                          fullWidth
                          size="small"
                          onClick={() => setDeliveryDatePickerOpen(true)}
                        />
                      )}
                      renderDay={getRenderDayWithTooltip(holidays, "delivery")}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="medium" gutterBottom>
                    Time Needed
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={deliveryTime}
                      onChange={handleDeliveryTimeChange}
                    >
                      {TimeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mb: 3 }}>
                <Typography fontWeight="600" gutterBottom fontSize={14}>
                  Order Notes: **Please do not enter credit card number or other
                  confidential information**
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              className="blueButtonOutline"
              onClick={handleContinue}
            >
              Continue
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PickupDelivery;
